import {usePasteFileSelect} from '@github-ui/use-unified-file-select'
import {useCallback, type ClipboardEventHandler, type RefObject} from 'react'

import {ACCEPTED_IMAGE_FILE_TYPES, useMarkdownImageUpload} from './use-markdown-image-upload'
import {useFileDropTarget, type DropTargetProps} from './use-file-drop-target'

export type UseMarkdownImageHandlingOptions = {
  textAreaRef: RefObject<HTMLTextAreaElement | null>
  repositoryId?: string
}

export type UseMarkdownImageHandlingReturn = {
  /** Pass as the `onPaste` prop on ChatInput. */
  onPaste: ClipboardEventHandler<HTMLTextAreaElement>
  /** Spread onto the drop target element for inline drag-and-drop support. */
  dropTargetProps: DropTargetProps
  /** True when a file is being dragged over the drop target. Use for visual feedback. */
  isDraggedOver: boolean
  isUploading: boolean
  addFiles: (files: File[]) => void
}

/**
 * A hook that provides `onPaste` and drag and drop handlers for a text area, implementing the
 * paste/drop → upload → markdown-inline lifecycle for images.
 *
 * Images are uploaded to dotcom's asset storage and inserted as standard markdown
 * `![filename](url)` syntax directly into the textarea text.
 *
 * The core upload logic is shared with `ImageAttachButton` via `useMarkdownImageUpload`.
 *
 * Usage:
 * ```tsx
 * const {onPaste, dropTargetProps, isUploading} = useMarkdownImageHandling({textAreaRef, repositoryId})
 * <textarea onPaste={onPaste} {...dropTargetProps} ref={textAreaRef} ... />
 * ```
 */
export function useMarkdownImageHandling({
  textAreaRef,
  repositoryId,
}: UseMarkdownImageHandlingOptions): UseMarkdownImageHandlingReturn {
  const {processFiles, isUploading} = useMarkdownImageUpload({
    textAreaRef,
    repositoryId,
  })

  const pasteTargetProps = usePasteFileSelect({
    onSelect: (accepted: File[]) => processFiles(accepted),
    multi: true,
    acceptedFileTypes: ACCEPTED_IMAGE_FILE_TYPES,
  })

  const onPaste: ClipboardEventHandler<HTMLTextAreaElement> = useCallback(
    event => {
      pasteTargetProps.onPaste(event)
    },
    [pasteTargetProps],
  )

  const {dropTargetProps, isDraggedOver} = useFileDropTarget(processFiles)

  return {
    onPaste,
    dropTargetProps,
    isDraggedOver,
    isUploading,
    addFiles: processFiles,
  }
}
